import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useCallback, useEffect, useId, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { X } from 'lucide-react';
import { cn } from '../lib/cn.js';
/**
 * Shuncha pastga surilsa panel yopiladi (px).
 *
 * Kichikroq bo'lsa ro'yxatni aylantirmoqchi bo'lgan barmoq panelni
 * tasodifan yopib yuboradi; kattaroq bo'lsa surish "ishlamayapti" bo'lib
 * tuyuladi va odam ikkinchi marta urinadi.
 */
const SWIPE_CLOSE_PX = 80;
const FOCUSABLE = [
    'a[href]',
    'button:not([disabled])',
    'input:not([disabled])',
    'select:not([disabled])',
    'textarea:not([disabled])',
    '[tabindex]:not([tabindex="-1"])',
].join(',');
const focusableIn = (root) => Array.from(root.querySelectorAll(FOCUSABLE)).filter((el) => el.getAttribute('aria-hidden') !== 'true');
const initialFocusIn = (root) => focusableIn(root).filter((el) => el.dataset.drawerClose !== 'true')[0] ?? null;
/**
 * O'ngdan chiquvchi panel — KO'P MAYDONLI formalar uchun.
 *
 * Nega modal emas: o'n beshta maydonli forma markazdagi oynada ekran
 * balandligini to'ldirib, ichida siljiy boshlaydi va odam qayerda
 * turganini yo'qotadi. Yon panel esa tabiiy ravishda uzun bo'ladi va
 * ortidagi jadval ko'rinib turadi — yozilayotgan ma'lumot qaysi qatorga
 * tegishli ekani ko'zdan ketmaydi.
 *
 * Telefonda pastdan to'liq ekran bo'lib chiqadi: 480px lik panel tor
 * ekranda baribir hamma joyni egallaydi, pastdan chiqishi esa bosh
 * barmoqqa yaqin.
 */
export const Drawer = ({ open, onClose, title, description, error, footer, busy = false, onSubmit, children, className, closeLabel = 'Yopish', sheet = false, }) => {
    const titleId = useId();
    const panelRef = useRef(null);
    const restoreRef = useRef(null);
    const grabRef = useRef(null);
    const [rendered, setRendered] = useState(open);
    const [closing, setClosing] = useState(false);
    /**
     * Barmoq bilan surilgan masofa. `null` — hech kim tegmagan.
     *
     * `active` — barmoq hali ekranda: shu paytda panel barmoqqa ANIQ
     * ergashishi kerak, ya'ni hech qanday o'tish (`transition`) bo'lmasin.
     * Qo'yib yuborilganda esa aksincha — joyiga qaytishi yoki pastga
     * chiqib ketishi silliq bo'ladi.
     */
    const [drag, setDrag] = useState(null);
    if (open && !rendered)
        setRendered(true);
    if (open && closing)
        setClosing(false);
    useEffect(() => {
        if (open || !rendered)
            return;
        setClosing(true);
        const timer = setTimeout(() => {
            setRendered(false);
            setClosing(false);
        }, 160);
        return () => clearTimeout(timer);
    }, [open, rendered]);
    useEffect(() => {
        if (!open)
            return;
        // Qayta ochilganda panel oldingi surilishini ESLAB QOLMASIN.
        grabRef.current = null;
        setDrag(null);
        const active = document.activeElement;
        restoreRef.current = active instanceof HTMLElement ? active : null;
        const panel = panelRef.current;
        if (panel)
            (initialFocusIn(panel) ?? panel).focus();
        const { body, documentElement } = document;
        const previousOverflow = body.style.overflow;
        const previousPadding = body.style.paddingRight;
        const gap = window.innerWidth - documentElement.clientWidth;
        body.style.overflow = 'hidden';
        if (gap > 0)
            body.style.paddingRight = `${gap}px`;
        return () => {
            body.style.overflow = previousOverflow;
            body.style.paddingRight = previousPadding;
            restoreRef.current?.focus();
        };
    }, [open]);
    const requestClose = useCallback(() => {
        if (!busy)
            onClose();
    }, [busy, onClose]);
    useEffect(() => {
        if (!open)
            return;
        const onKeyDown = (event) => {
            if (event.key !== 'Escape')
                return;
            event.stopPropagation();
            requestClose();
        };
        document.addEventListener('keydown', onKeyDown);
        return () => document.removeEventListener('keydown', onKeyDown);
    }, [open, requestClose]);
    /*
     * SURIB YOPISH — faqat ushlagichdan.
     *
     * Ishlovchilar panelning o'ziga emas, aynan ushlagichga osilgan: ro'yxat
     * ustidan surilganda barmoq ro'yxatni aylantirishi kerak, panelni emas.
     * Ikkovini bitta yuzada birlashtirish "aylantirmoqchi edim — yopilib
     * ketdi" degan eng jonga tegadigan xatoni tug'diradi.
     *
     * Ushlagich `sm:hidden`, ya'ni keng ekranda umuman yo'q — u yerda panel
     * yon tomondan chiqadi va pastga surish ma'nosiz bo'lardi.
     */
    const onGrabStart = useCallback((event) => {
        if (busy)
            return;
        grabRef.current = event.touches[0]?.clientY ?? null;
        setDrag({ y: 0, active: true });
    }, [busy]);
    const onGrabMove = useCallback((event) => {
        const start = grabRef.current;
        if (start === null)
            return;
        // Faqat PASTGA. Yuqoriga tortish panelni cho'zmaydi — balandligi
        // kontentdan kelib chiqadi va cho'zilgan panel ostida oq chiziq qolardi.
        const moved = (event.touches[0]?.clientY ?? start) - start;
        setDrag({ y: Math.max(0, moved), active: true });
    }, []);
    const onGrabEnd = useCallback(() => {
        if (grabRef.current === null)
            return;
        grabRef.current = null;
        const moved = drag?.y ?? 0;
        // Chegaraga yetmadi (yoki yuborish ketmoqda) — panel joyiga qaytadi.
        if (moved < SWIPE_CLOSE_PX || busy) {
            setDrag({ y: 0, active: false });
            return;
        }
        // Yetdi: panel BOSHLAGAN yo'nalishida davom etib pastga chiqadi.
        // Joyiga qaytib, keyin yopilsa, harakat sakrab ko'rinardi.
        setDrag({ y: panelRef.current?.offsetHeight ?? moved, active: false });
        requestClose();
    }, [busy, drag, requestClose]);
    const onPanelKeyDown = useCallback((event) => {
        if (event.key === 'Enter' && (event.metaKey || event.ctrlKey)) {
            if (onSubmit && !busy) {
                event.preventDefault();
                onSubmit();
            }
            return;
        }
        if (event.key !== 'Tab')
            return;
        const panel = panelRef.current;
        if (!panel)
            return;
        const items = focusableIn(panel);
        const first = items[0];
        const last = items[items.length - 1];
        if (!first || !last) {
            event.preventDefault();
            panel.focus();
            return;
        }
        const active = document.activeElement;
        if (event.shiftKey && (active === first || active === panel)) {
            event.preventDefault();
            last.focus();
        }
        else if (!event.shiftKey && active === last) {
            event.preventDefault();
            first.focus();
        }
    }, [busy, onSubmit]);
    if (!rendered)
        return null;
    /*
     * Panelga tegilgan bo'lsa, kirish/chiqish ANIMATSIYASI o'chadi.
     *
     * Sababi CSS ustuvorligida: animatsiya `both` bilan tugagach ham o'z
     * `transform` ini ushlab turadi va u inline uslubdan KUCHLIROQ. Ikkovi
     * bir vaqtda qolsa, barmoq panelni surardi-yu, panel joyidan
     * qimirlamasdi.
     */
    const animation = drag !== null ? undefined : closing ? 'motion-drawer-out' : 'motion-drawer-in';
    const dragStyle = drag === null
        ? undefined
        : {
            transform: `translateY(${drag.y}px)`,
            transition: drag.active
                ? 'none'
                : 'transform var(--motion-modal-out) var(--ease-accelerate)',
        };
    return createPortal(_jsx("div", { className: cn(closing ? 'motion-backdrop-out' : 'motion-backdrop-in', 'fixed inset-0 z-[var(--z-modal)] flex items-end justify-end sm:items-stretch', 'bg-[var(--color-overlay)]'), onMouseDown: (event) => {
            if (event.target === event.currentTarget)
                requestClose();
        }, children: _jsxs("div", { ref: panelRef, role: "dialog", "aria-modal": "true", "aria-labelledby": titleId, "aria-busy": busy || undefined, tabIndex: -1, onKeyDown: onPanelKeyDown, style: dragStyle, className: cn(animation, 'flex w-full flex-col overflow-hidden bg-surface', 
            // Ro'yxat pastroq turadi: ortida sahifaning bir qismi ko'rinib
            // tursin. Forma esa ekranni to'ldiradi — unda maydon ko'p.
            sheet ? 'max-h-[85vh]' : 'max-h-[92vh]', 'rounded-t-[var(--radius-lg)] shadow-[var(--shadow-3)]', 
            // Katta ekranda: to'liq balandlik, o'ng chetga yopishgan.
            'sm:max-h-none sm:h-full sm:w-[var(--size-drawer)] sm:rounded-none', className), children: [sheet ? (_jsx("div", { "data-drawer-grip": "true", onTouchStart: onGrabStart, onTouchMove: onGrabMove, onTouchEnd: onGrabEnd, onTouchCancel: onGrabEnd, 
                    /* `touch-none` — surish paytida brauzer sahifani o'zi
                       aylantirib yubormasin: ikkovi bir vaqtda bo'lsa harakat
                       qaltiraydi. */
                    className: "flex shrink-0 touch-none justify-center pt-2.5 pb-1 sm:hidden", children: _jsx("span", { "aria-hidden": true, className: "h-1 w-10 rounded-[var(--radius-pill)] bg-border-strong" }) })) : null, _jsxs("header", { className: cn('flex items-start justify-between gap-4 border-b border-border px-6 py-5', sheet ? 'pt-3 sm:pt-5' : undefined), children: [_jsxs("div", { className: "flex min-w-0 flex-col gap-1", children: [_jsx("h2", { id: titleId, className: "text-xl font-semibold tracking-tight text-text", children: title }), description ? _jsx("p", { className: "text-sm text-text-muted", children: description }) : null] }), _jsx("button", { type: "button", "data-drawer-close": "true", onClick: requestClose, disabled: busy, "aria-label": closeLabel, className: cn('-mr-1.5 -mt-1 flex size-9 shrink-0 items-center justify-center', 'rounded-[var(--radius-sm)] text-text-faint', 'transition-[background-color,color] duration-[var(--motion-fast)]', 'ease-[var(--ease-standard)] hover:bg-surface-2 hover:text-text', 'disabled:cursor-not-allowed disabled:opacity-50'), children: _jsx(X, { size: 20, "aria-hidden": true }) })] }), error ? (_jsx("div", { role: "alert", className: cn('motion-field-error mx-6 mt-4 rounded-[var(--radius-sm)]', 'border border-danger/25 bg-danger-soft px-3.5 py-3 text-sm text-danger'), children: error })) : null, _jsx("div", { className: "min-h-0 flex-1 overflow-y-auto px-6 py-6 text-base text-text", children: children }), footer ? (_jsx("footer", { className: cn('flex flex-col-reverse gap-2 border-t border-border px-6 py-5', 'sm:flex-row sm:items-center sm:justify-end sm:py-4', '[&>*]:w-full sm:[&>*]:w-auto'), children: footer })) : null] }) }), document.body);
};
//# sourceMappingURL=Drawer.js.map